import React from 'react';
import { motion } from 'framer-motion';

import { NextBestAction, NextBestActionType } from './types';
import { MemoizedIcon } from '../ui/icon';
import { Button } from '../ui/button';
import { addPopupWidgetUtm } from '../../lib/addUtmParams';

const MotionButton = motion(Button);

// Helper to close Brainfish widget before launching third-party widgets
const closeBrainfishWidget = () => {
  if (window.Brainfish?.HelpWidget?.close) {
    window.Brainfish.HelpWidget.close();
  }
};

// Reusable action button component for widget integrations
const ActionButton: React.FC<{
  icon?: string;
  label: string;
  index: number;
  onClick: () => void;
  showArrowOut?: boolean;
  asChild?: boolean;
  children?: React.ReactNode;
}> = ({ icon, label, index, onClick, showArrowOut = false, asChild = false, children }) => (
  <MotionButton
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.1 }}
    className="w-full text-left h-12 text-base [&_svg]:size-6"
    variant="ghost"
    onClick={onClick}
    asChild={asChild}
  >
    {asChild ? (
      children
    ) : (
      <>
        {icon && <MemoizedIcon iconName={icon} />}
        <span className="flex-grow">{label}</span>
        {showArrowOut && <MemoizedIcon iconName="ArrowSquareOut" />}
      </>
    )}
  </MotionButton>
);

// Reusable action link component for email, link, phone
const ActionLink: React.FC<{
  href: string;
  icon?: string;
  label: string;
  index: number;
  onClick: () => void;
}> = ({ href, icon, label, index, onClick }) => (
  <ActionButton icon={icon} label={label} index={index} onClick={onClick} asChild>
    <a href={href} target="_blank" rel="noopener noreferrer">
      {icon && <MemoizedIcon iconName={icon} />}
      <span className="flex-grow">{label}</span>
      <MemoizedIcon iconName="ArrowSquareOut" />
    </a>
  </ActionButton>
);

interface NextBestActionsProps {
  nextBestActions: NextBestAction[];
  onActionClick: (action: NextBestAction, success?: boolean) => void;
}

const NextBestActions: React.FC<NextBestActionsProps> = ({ nextBestActions, onActionClick }) => {
  if (!nextBestActions.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full"
    >
      <div className="rounded-lg overflow-hidden bg-card border  divide-y divide-dark-300">
        {nextBestActions.map((action, index) => {
          const handleClick = (success?: boolean) => {
            onActionClick(action, success);
          };

          // create an key for each action based on label and index
          const key = `${action.label}-${index}`.replace(/\s+/g, '-');

          switch (action.type) {
            case NextBestActionType.Email:
              return (
                <ActionLink
                  key={key}
                  href={`mailto:${action.value}`}
                  icon={action.icon}
                  label={action.label}
                  index={index}
                  onClick={() => handleClick()}
                />
              );

            case NextBestActionType.Link:
              return (
                <ActionLink
                  key={key}
                  href={addPopupWidgetUtm(action.value)}
                  icon={action.icon}
                  label={action.label}
                  index={index}
                  onClick={() => handleClick()}
                />
              );

            case NextBestActionType.Phone:
              return (
                <ActionLink
                  key={key}
                  href={`tel:${action.value}`}
                  icon={action.icon}
                  label={action.label}
                  index={index}
                  onClick={() => handleClick()}
                />
              );

            case NextBestActionType.ZendeskChat:
              return (
                <ActionButton
                  key={key}
                  icon={action.icon}
                  label={action.label}
                  index={index}
                  onClick={() => {
                    // Zendesk Chat (Classic) uses webWidget namespace
                    if (window.zE) {
                      closeBrainfishWidget();
                      window.zE('webWidget', 'open');
                      handleClick(true);
                    } else {
                      console.error(
                        "Zendesk Chat is not loaded. Make sure you've included the Zendesk Chat script in your website.",
                      );
                      handleClick(false);
                    }
                  }}
                />
              );

            case NextBestActionType.ZendeskMessenger:
              return (
                <ActionButton
                  key={key}
                  icon={action.icon}
                  label={action.label}
                  index={index}
                  onClick={() => {
                    // Zendesk Messenger uses messenger namespace
                    if (window.zE) {
                      closeBrainfishWidget();
                      window.zE('messenger', 'open');
                      handleClick(true);
                    } else {
                      console.error(
                        "Zendesk Messenger is not loaded. Make sure you've included the Zendesk Messenger script in your website.",
                      );
                      handleClick(false);
                    }
                  }}
                />
              );

            case NextBestActionType.IntercomChat:
              return (
                <ActionButton
                  key={key}
                  icon={action.icon}
                  label={action.label}
                  index={index}
                  onClick={() => {
                    if (window.Intercom) {
                      closeBrainfishWidget();
                      window.Intercom('show');
                      handleClick(true);
                    } else {
                      console.error(
                        "Intercom is not loaded. Make sure you've included the Intercom script in your website.",
                      );
                      handleClick(false);
                    }
                  }}
                />
              );

            case NextBestActionType.Freshdesk:
              return (
                <ActionButton
                  key={key}
                  icon={action.icon}
                  label={action.label}
                  index={index}
                  onClick={() => {
                    if (window.fcWidget) {
                      closeBrainfishWidget();
                      setTimeout(() => {
                        window.fcWidget!.open();
                        window.fcWidget!.show();
                      }, 300);
                      handleClick(true);
                    } else {
                      console.error(
                        "Freshdesk is not loaded. Make sure you've included the Freshdesk script in your website.",
                      );
                      handleClick(false);
                    }
                  }}
                />
              );

            case NextBestActionType.ZendeskSunshineChat:
              return (
                <ActionButton
                  key={key}
                  icon="CalendarBlank"
                  label={action.label}
                  index={index}
                  onClick={() => handleClick()}
                />
              );

            case NextBestActionType.WebhookHandoff:
              return (
                <ActionButton
                  key={key}
                  icon="CalendarBlank"
                  label={action.label}
                  index={index}
                  onClick={() => handleClick()}
                />
              );

            default:
              return (
                <ActionButton
                  key={key}
                  icon="ArrowCircleRight"
                  label={action.label}
                  index={index}
                  onClick={() => handleClick()}
                  showArrowOut={true}
                />
              );
          }
        })}
      </div>
    </motion.div>
  );
};

export { NextBestActions };
